import type { ReactNode } from 'react';

/**
 * Card surface — the Aurora-styled replacement for the inline white-
 * card pattern that appears 40+ times across the app:
 *
 *   <div className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
 *
 * Composable: render <CardHeader />, <CardSection />, or your own
 * children inside. Use `padded={false}` when you want an edge-to-edge
 * section header followed by a list with its own dividers.
 *
 * Tones reuse the soft status tokens for subtly-coloured cards (e.g.
 * the "checked in" panel uses `tone="success"`).
 */

type Tone = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const TONE_BG: Record<Tone, string> = {
  default: 'bg-surface',
  success: 'bg-success-soft/10',
  warning: 'bg-warning-soft/10',
  danger: 'bg-danger-soft/10',
  info: 'bg-info-soft/10',
  accent: 'bg-accent/5',
};

export function Card({
  children,
  className = '',
  padded = false,
  tone = 'default',
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  tone?: Tone;
}) {
  return (
    <section
      className={[
        'overflow-hidden rounded-token-lg border border-line/10 shadow-token-sm',
        TONE_BG[tone],
        padded ? 'p-[var(--space-card)]' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </section>
  );
}

/**
 * The icon + title + (optional) trailing slot pattern used at the top
 * of most cards in the preview. Sized for the default card; nests
 * naturally inside <Card padded={false}>.
 */
export function CardHeader({
  icon,
  title,
  description,
  trailing,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-3 border-b border-divider/10 px-5 py-4">
      <div className="flex min-w-0 items-center gap-2.5">
        {icon ? (
          <span
            aria-hidden
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-token-sm bg-accent/10 text-accent"
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-fg">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
          ) : null}
        </div>
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </header>
  );
}

/**
 * Inner body section with default padding. Use when a card contains
 * more than one logical region separated by a divider.
 */
export function CardSection({
  children,
  className = '',
  divided = false,
}: {
  children: ReactNode;
  className?: string;
  divided?: boolean;
}) {
  return (
    <div
      className={[
        'px-5 py-4',
        divided ? 'border-t border-divider/10' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
